import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { ListJobsService } from "@application/useCases/job/ListJobTasksService";
import {
  getSelectedState,
  getSelectedStateFromKeyDown,
  Grid,
  GridColumn,
  GRID_PREVENT_SELECTION_ELEMENT,
  GridCustomCellProps,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridHandle,
  GridKeyDownEvent,
  GridRowClickEvent,
  GridSelectionChangeEvent,
} from "@progress/kendo-react-grid";
import { Loader } from "@progress/kendo-react-indicators";
import { useInjection } from "@presentation/components/dependencyInjection";
import { Dialog } from "@progress/kendo-react-dialogs";
import { useJobListStore } from "@presentation/stores";
import { useShallow } from "zustand/react/shallow";
import { JobListItemState } from "@presentation/common/types/extensions/JobListItemState";
import { process, DataResult, CompositeFilterDescriptor, FilterDescriptor } from "@progress/kendo-data-query";
import { DropdownFilterCell } from "@presentation/components/DropdownFilterCell";
import { StatusEnumSchema, JobPipelineTypeSchema } from "@domain/schemas/BI";
import type { JobPipelineStepDetail, SoftwareEnumSchema } from "@domain/schemas/BI";
import { getPipelineLabel, getStepLabel } from "@domain/software";
import { Button, Toolbar } from "@progress/kendo-react-buttons";
import { DATA_ITEM_KEY, EXPANDED_FIELD, SELECTED_FIELD, idGetter } from "@presentation/components/helpers/constants";
import { useTranslation } from "react-i18next";
import { StepsView } from "./components/StepsView";
import StatusCell from "./components/StatusCell";
import { trashIcon, cancelIcon, playIcon, downloadIcon, filterIcon, copyIcon } from "@progress/kendo-svg-icons";
import { DownloadResultsService } from "@application/useCases";
import { useNavigate } from "react-router-dom";
import { SamplesView } from "./components/SamplesView";
import { pauseIcon } from "@presentation/icons/pause";
import { DuplicateJobMetadata, getDuplicateJobMetadataMap } from "@presentation/common";
import { subscribeToJobsList, subscribeToRefreshList } from "@infrastructure/realtime/jobsHubClient";
import { useArtifactDownloadDialog } from "./components/useArtifactDownloadDialog";
import { useBatchArtifactsViewer } from "./components/useBatchArtifactsViewer";
import { useFailedBatchesDialog } from "./components/useFailedBatchesDialog";
import { JobStatusLogsCell } from "./components/JobStatusLogsCell";

/**
 * Create a context to share with the child/slave components the mutable value of isDropdownFilterFocusedRef in real time.
 */
export const DropdownFilterFocusContext = createContext({
  isDropdownFilterFocusedRef: {
    current: false,
  },
  showFilters: false,
});

export const BatchArtifactsViewerContext = createContext({
  openLogs: async (_jobId: string, _jobPipelineStepId: string, _batch: { id: string; status?: StatusEnumSchema | null }) => {},
  openResults: async (_jobId: string, _jobPipelineStepId: string, _batch: { id: string; status?: StatusEnumSchema | null }) => {},
});

type BatchArtifactsViewerActions = {
  openLogs: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) => Promise<void>;
  openResults: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) => Promise<void>;
};

/**
 * `isParallel` and `status` are only exposed by the job step list endpoint (`JobPipelineStepDetail`);
 * the single-step endpoint returns the leaner `StepSchema`, which carries neither.
 */
type CurrentStepDetails = Pick<JobPipelineStepDetail, "type" | "isParallel" | "status">;

type GridColumnHandle = {
  id?: string;
  field?: string;
  children?: GridColumnHandle[];
};

type GridAutoFitHandle = GridHandle & {
  columns?: GridColumnHandle[];
  fitColumns?: (columnIds: string[]) => void;
};

const BatchArtifactsViewerHost: React.FC<{
  listJobsService: ListJobsService;
  actionsRef: React.MutableRefObject<BatchArtifactsViewerActions>;
}> = ({ listJobsService, actionsRef }) => {
  const { artifactsDialog, openLogs, openResults } = useBatchArtifactsViewer(listJobsService);

  actionsRef.current = { openLogs, openResults };

  return <>{artifactsDialog}</>;
};

/**
 * Grid columns backed by a derived `virtual*` Date field cannot be sent to the API as-is, because the
 * backend sorts on the underlying job property. Map them back before building the request.
 */
const SORT_FIELD_MAP: Record<string, string> = {
  virtualCreatedAt: "createdAt",
  virtualStartedAt: "startedAt",
  virtualFinishedAt: "finishedAt",
};

const toApiSortField = (field?: string): string | undefined => (field ? (SORT_FIELD_MAP[field] ?? field) : undefined);

/**
 * Matches the `{0:dd/MM/yyyy h:mm a}` Kendo format used by the step grid, so job and step timestamps
 * read identically.
 */
const formatGridDateTime = (value: Date | string): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const meridiem = date.getHours() >= 12 ? "PM" : "AM";
  const hours = date.getHours() % 12 || 12;

  return `${day}/${month}/${date.getFullYear()} ${hours}:${minutes} ${meridiem}`;
};

const findFilterValue = (filter: CompositeFilterDescriptor | FilterDescriptor | undefined, fieldName: string): string | undefined => {
  if (!filter) {
    return undefined;
  }

  if ("filters" in filter) {
    for (const nestedFilter of filter.filters) {
      const value = findFilterValue(nestedFilter as CompositeFilterDescriptor | FilterDescriptor, fieldName);
      if (value !== undefined) {
        return value;
      }
    }

    return undefined;
  }

  return filter.field === fieldName && typeof filter.value === "string" ? filter.value : undefined;
};

// Job file fields can come back as a joined string or an array, optionally prefixed with their storage folder.
const formatFileListDisplay = (value: string | string[] | undefined | null, stripPrefix?: RegExp): string => {
  if (Array.isArray(value)) {
    return stripPrefix ? value.map((file) => file.replace(stripPrefix, "")).join("\n") : value.join("\n");
  }
  if (typeof value === "string") {
    return stripPrefix ? value.replace(stripPrefix, "") : value;
  }
  return "";
};

const JobList: React.FC = () => {
  const { metaState, setMetaState, jobList, setJobList, setIsExpanded, selectedState, setSelectedState } = useJobListStore(
    useShallow((state) => ({
      metaState: state.metaState,
      setMetaState: state.setMetaState,
      jobList: state.jobList,
      setJobList: state.setJobList,
      setIsExpanded: state.setIsExpanded,
      selectedState: state.selectedState,
      setSelectedState: state.setSelectedState,
    })),
  );

  const listJobsService = useInjection(ListJobsService);
  const downloadResultsService = useInjection(DownloadResultsService);
  const [selectedJob, setSelectedJob] = useState<JobListItemState | null>(null);
  const [dataResult, setDataResult] = useState<DataResult>();
  const [isDoubleClickDialogVisible, setIsDoubleClickDialogVisible] = useState(false);
  const [selectedSampleBatchId, setSelectedSampleBatchId] = useState<string | null>(null);
  const [selectedSampleJobName, setSelectedSampleJobName] = useState<string | null>(null);
  const { t: loc } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [duplicateJobMetadataMap, setDuplicateJobMetadataMap] = useState<Record<string, DuplicateJobMetadata>>({});
  const navigate = useNavigate();
  const gridReference = useRef<GridAutoFitHandle | null>(null);
  const hasAutoFittedNameColumnReference = useRef(false);
  const batchArtifactsActionsReference = useRef<BatchArtifactsViewerActions>({
    openLogs: async () => {},
    openResults: async () => {},
  });
  const { artifactDownloadDialog, openJobDownloadDialog } = useArtifactDownloadDialog({ downloadResultsService });

  /**
   * Ref used to determine whether any dropdown filter is currently focused. Refreshing the parent
   * component makes the filter lose focus, which is disruptive while the user is typing, so refreshes
   * are skipped while this is true.
   */
  const isDropdownFilterFocusedReference = useRef(false);
  const isFetchingReference = useRef(false);

  const contextValue = React.useMemo(
    () => ({
      isDropdownFilterFocusedRef: isDropdownFilterFocusedReference,
      showFilters,
    }),
    [showFilters],
  );

  const batchArtifactsContextValue = React.useMemo(
    () => ({
      openLogs: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) => batchArtifactsActionsReference.current.openLogs(jobId, jobPipelineStepId, batch),
      openResults: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) =>
        batchArtifactsActionsReference.current.openResults(jobId, jobPipelineStepId, batch),
    }),
    [],
  );
  const { failedBatchesDialog, openFailedBatchesDialog } = useFailedBatchesDialog({ listJobsService, openLogs: batchArtifactsContextValue.openLogs });

  const handleDropdownToggle = (isFocused: boolean) => {
    // Set the current value of the ref to the new focus state
    isDropdownFilterFocusedReference.current = isFocused;
  };

  const fetchCurrentStepDetails = useCallback(
    async (jobId: string, jobPipelineStepId: string): Promise<CurrentStepDetails | null> => {
      try {
        const steps = await listJobsService.getJobStepListAsync(jobId);
        return steps.find((step) => step.id === jobPipelineStepId) ?? null;
      } catch (error) {
        console.error(`Error fetching step details for ${jobPipelineStepId}:`, error);
        return null;
      }
    },
    [listJobsService],
  );

  const fetchJobs = useCallback(async () => {
    // Only fetch jobs if no dropdown filter is focused and not already fetching
    if (isDropdownFilterFocusedReference.current || isFetchingReference.current) return;

    isFetchingReference.current = true;
    try {
      const take = metaState.take || 20;
      const skip = metaState.skip || 0;

      // Kendo -> API conversion
      const pageNumber = Math.floor(skip / take) + 1;

      const sort = metaState.sort?.[0];
      let sortDirection: "Ascending" | "Descending" | undefined;
      if (sort?.dir === "asc") sortDirection = "Ascending";
      else if (sort?.dir === "desc") sortDirection = "Descending";

      // Look for the filter applied to the "name" column
      const jobNameFilterValue = findFilterValue(metaState.filter as CompositeFilterDescriptor | undefined, "name");
      const jobCreatedByFilterValue = findFilterValue(metaState.filter as CompositeFilterDescriptor | undefined, "createdBy");

      const pagedJobListResponse = await listJobsService.getJobListAsync(pageNumber, take, toApiSortField(sort?.field), sortDirection, jobNameFilterValue, jobCreatedByFilterValue);
      const jobListResponse = pagedJobListResponse.list || [];

      const updatedJobList = await Promise.all(
        jobListResponse.map(async (job) => {
          const virtualCreatedAt = job.createdAt ? new Date(job.createdAt) : job.createdAt;
          const virtualStartedAt = job.startedAt ? new Date(job.startedAt) : job.startedAt;
          const virtualFinishedAt = job.finishedAt ? new Date(job.finishedAt) : job.finishedAt;
          let currentStep = "";
          let currentStepIsParallel: boolean | undefined;
          let currentStepStatus: StatusEnumSchema | undefined;

          if (job.currentStep && (job.status === StatusEnumSchema.Running || job.status === StatusEnumSchema.Pending)) {
            // Get latest jobList from store to avoid stale closure
            const currentJobList = useJobListStore.getState().jobList;
            // Find matching job in the current list
            const previousJob = currentJobList.find((previousJob_) => previousJob_.id === job.id);

            // Fetch step type when:
            // 1. We don't have a previous job
            // 2. The step ID has changed (job.currentStep vs prevJob.currentStepId)
            // 3. Or we don't have a currentStep name from before
            const previousStepId = previousJob?.currentStepId;
            if (!previousJob || job.currentStep !== previousStepId || !previousJob.currentStep) {
              const stepDetails = await fetchCurrentStepDetails(job.id, job.currentStep.toString());
              currentStep = formatStepName(stepDetails?.type, job.software);
              currentStepIsParallel = stepDetails?.isParallel;
              currentStepStatus = stepDetails?.status;
            } else {
              // Use the previously fetched step type
              currentStep = previousJob.currentStep;
              currentStepIsParallel = previousJob.currentStepIsParallel;
              currentStepStatus = previousJob.currentStepStatus;
            }
          }

          return {
            ...job,
            virtualCreatedAt,
            virtualStartedAt,
            virtualFinishedAt,
            currentStep,
            currentStepId: job.currentStep, // Store the ID to detect changes next poll
            currentStepIsParallel,
            currentStepStatus,
            progress: job.progress || 0,
          };
        }),
      );

      setJobList(updatedJobList);
      setLoading(false);
      setTotalJobs(pagedJobListResponse.totalCount || 0);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setLoading(false);
    } finally {
      isFetchingReference.current = false;
    }
  }, [fetchCurrentStepDetails, isDropdownFilterFocusedReference, listJobsService, metaState]);

  // Initial load and manual refresh (on metaState change)
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Keep a ref to fetchJobs so the SignalR effect doesn't depend on it
  // and doesn't reconnect every time fetchJobs changes.
  const fetchJobsReference = useRef(fetchJobs);
  useEffect(() => {
    fetchJobsReference.current = fetchJobs;
  }, [fetchJobs]);

  useEffect(() => {
    const unsubscribeFromJobsList = subscribeToJobsList();
    const unsubscribeFromRefreshList = subscribeToRefreshList(() => {
      fetchJobsReference.current();
    });

    return () => {
      unsubscribeFromRefreshList();
      unsubscribeFromJobsList();
    };
  }, []);

  useEffect(() => {
    setDuplicateJobMetadataMap(getDuplicateJobMetadataMap());
  }, [jobList]);

  useEffect(() => {
    if (loading || hasAutoFittedNameColumnReference.current || !dataResult?.data?.length) {
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      const nameColumnId = gridReference.current?.columns?.find((column) => column.field === "name" && !column.children?.length)?.id;
      if (!nameColumnId || !gridReference.current?.fitColumns) {
        return;
      }

      gridReference.current.fitColumns([nameColumnId]);
      hasAutoFittedNameColumnReference.current = true;
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [dataResult, loading]);

  useEffect(() => {
    const dataWithSelection = jobList.map((item) => {
      const selection = selectedState[idGetter(item)];

      return {
        ...item,
        [SELECTED_FIELD]: Array.isArray(selection) ? selection.length > 0 : Boolean(selection),
      };
    });

    const processed = process(dataWithSelection, {
      filter: metaState.filter,
    });

    setDataResult({
      data: processed.data,
      total: totalJobs,
    });
  }, [jobList, selectedState, totalJobs]);

  if (loading && jobList.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <Loader size="large" type="infinite-spinner" />
      </div>
    );
  }

  const closeDialog = () => {
    setIsDoubleClickDialogVisible(false);
  };

  const handleDataStateChange = (e: GridDataStateChangeEvent) => {
    const { dataState } = e;
    const isFilterChanged = JSON.stringify(metaState.filter ?? null) !== JSON.stringify(dataState.filter ?? null);

    setMetaState({
      ...metaState,
      skip: isFilterChanged ? 0 : (dataState.skip ?? 0),
      take: dataState.take ?? 20,
      sort: dataState.sort,
      filter: dataState.filter,
    });
  };

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setSelectedState(newSelectedState);
  };

  const handleKeyDown = (event: GridKeyDownEvent) => {
    const newSelectedState = getSelectedStateFromKeyDown({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setSelectedState(newSelectedState);
  };

  const openJobDetails = (job: JobListItemState) => {
    setSelectedJob(job);
    setIsDoubleClickDialogVisible(true);
  };

  const handleCellClick = (properties: GridCustomCellProps, event: React.MouseEvent<HTMLTableCellElement>) => {
    if (properties.tdProps?.onClick) {
      properties.tdProps.onClick(event);
    } else {
      properties.selectionChange?.({ syntheticEvent: event });
    }

    if (event.detail === 2) {
      openJobDetails(properties.dataItem);
    }
  };

  const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>) => {
    return React.cloneElement(trElement, {
      ...trElement.props,
      title: loc("doubleClickForDetails"),
    });
  };

  const handleExpandChange = async (event: GridExpandChangeEvent) => {
    event.dataItem.expanded = event.value;
    if (!event.dataItem.id) return;

    const expandedJob: JobListItemState = event.dataItem;
    setIsExpanded(expandedJob.id, event.value);
  };

  const handleDownload = async (dataItem: any) => {
    try {
      await openJobDownloadDialog(dataItem.id);
    } catch {
      alert(`Failed to download job results.`);
    }
  };

  const handleSamplesClick = async (job: JobListItemState) => {
    try {
      const steps = await listJobsService.getJobStepListAsync(job.id);
      const candidateStep = steps.find((step) => !step.isParallel && (step.batchesCount ?? 0) === 1);

      if (!candidateStep) {
        alert("No single-batch step samples are available for this job.");
        return;
      }

      const batchResponse = await listJobsService.getJobStepBatchListAsync(job.id, candidateStep.id);
      const batchId = batchResponse.list?.[0]?.id;
      if (!batchId) {
        alert("No batch found for the selected step.");
        return;
      }

      setSelectedSampleBatchId(batchId);
      setSelectedSampleJobName(job.name);
    } catch (error) {
      console.error("Error loading samples:", error);
    }
  };

  const closeSamplesDialog = () => {
    setSelectedSampleBatchId(null);
    setSelectedSampleJobName(null);
  };

  const openDuplicateJobSetup = (jobId: string) => {
    navigate(`/job-setup?rerunJobId=${jobId}`);
  };

  const getDuplicateMetadata = (jobId: string) => duplicateJobMetadataMap[jobId] ?? null;

  const getDuplicateSummary = (metadata: DuplicateJobMetadata | null) => {
    if (!metadata) return null;

    return {
      sourceLabel: metadata.sourceJobName,
    };
  };

  const selectedJobIds = Object.keys(selectedState).filter((id) => selectedState[id]);
  const hasSelectedJobs = selectedJobIds.length > 0;
  const selectedDuplicableJobs = selectedJobIds.map((jobId) => jobList.find((job) => job.id === jobId)).filter((job): job is JobListItemState => !!job);

  return (
    <div className="fill-all" style={{ overflow: "hidden" }}>
      {artifactDownloadDialog}
      {failedBatchesDialog}
      <Toolbar className="page-toolbar" style={{ border: "none", background: "transparent", marginLeft: "-14px" }}>
        <Button
          svgIcon={pauseIcon}
          themeColor="warning"
          title="Pause selected jobs"
          disabled={!hasSelectedJobs}
          onClick={async () => {
            const confirmed = confirm("Are you sure you want to pause the selected jobs? This will stop non-running batches while preserving completed and recoverable progress.");
            if (!confirmed) return;

            try {
              await Promise.all(selectedJobIds.map((jobId) => listJobsService.cancelPendingBatchesByJobIDAsync(jobId)));
              fetchJobs();
            } catch (error) {
              console.error("Error pausing jobs:", error);
            }
          }}
        />
        <Button
          svgIcon={playIcon}
          themeColor="info"
          title="Resume selected jobs"
          disabled={!hasSelectedJobs}
          style={{ marginLeft: "-2px" }}
          onClick={async () => {
            const confirmed = confirm("Are you sure you want to resume the selected jobs? Only paused, aborted or failed jobs can be resumed.");
            if (!confirmed) return;

            try {
              await Promise.all(selectedJobIds.map((jobId) => listJobsService.resumeJobByIDAsync(jobId)));
              fetchJobs();
            } catch (error) {
              console.error("Error resuming jobs:", error);
            }
          }}
        />
        <Button
          svgIcon={cancelIcon}
          themeColor="error"
          title="Abort selected jobs"
          disabled={!hasSelectedJobs}
          style={{ marginLeft: "-2px" }}
          onClick={async () => {
            const confirmed = confirm("Are you sure you want to abort the selected jobs? This will stop the execution of all their active batches(Scheduled, Pending or Running).");
            if (!confirmed) return;

            try {
              await Promise.all(selectedJobIds.map((jobId) => listJobsService.abortJobByIDAsync(jobId)));
              fetchJobs();
            } catch (error) {
              console.error("Error aborting jobs:", error);
            }
          }}
        />
        <Button
          svgIcon={copyIcon}
          title="Duplicate selected job"
          disabled={selectedDuplicableJobs.length !== 1}
          style={{ marginLeft: "-2px" }}
          onClick={() => openDuplicateJobSetup(selectedDuplicableJobs[0].id)}
        />
        <Button
          svgIcon={trashIcon}
          title="Delete selected jobs"
          disabled={!hasSelectedJobs}
          style={{ marginLeft: "-2px" }}
          onClick={async () => {
            const confirmed = confirm("Are you sure you want to delete the selected jobs? This will also remove the folder containing the results.");
            if (!confirmed) return;

            const finishedJobIds = selectedJobIds.filter((jobId) => {
              const job = jobList.find((job) => job.id === jobId);
              return job?.status === StatusEnumSchema.Completed || job?.status === StatusEnumSchema.Failed || job?.status === StatusEnumSchema.Aborted || job?.status === StatusEnumSchema.Paused;
            });

            if (finishedJobIds.length < selectedJobIds.length) {
              alert("You can only delete not active jobs. Some selected jobs are still active and will not be deleted. To delete them you must abort them first or let them finish");
            }

            try {
              await Promise.all(finishedJobIds.map((jobId) => listJobsService.deleteJobByIDAsync(jobId)));
              fetchJobs();
            } catch (error) {
              console.error("Error deleting jobs:", error);
            }
          }}
        />
        <Button
          svgIcon={filterIcon}
          title="Toggle Filter Row"
          togglable={true}
          selected={showFilters}
          style={{ marginLeft: "-2px" }}
          onClick={() => {
            setShowFilters((previous) => !previous);
          }}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </Toolbar>
      <BatchArtifactsViewerContext.Provider value={batchArtifactsContextValue}>
        <BatchArtifactsViewerHost listJobsService={listJobsService} actionsRef={batchArtifactsActionsReference} />
        <DropdownFilterFocusContext.Provider value={contextValue}>
          <Grid
            ref={gridReference}
            className="page-content-with-toolbar"
            resizable={true}
            dataItemKey={DATA_ITEM_KEY}
            selectedField={SELECTED_FIELD}
            expandField={EXPANDED_FIELD}
            scrollable="scrollable"
            navigatable={true}
            filterable={showFilters}
            sortable={true}
            pageable={{
              buttonCount: 5,
              pageSizes: [1, 10, 20, 50, 100],
              previousNext: true,
              info: true,
            }}
            skip={metaState.skip || 0}
            take={metaState.take || 20}
            selectable={{
              enabled: true,
              drag: false,
              mode: "multiple",
            }}
            {...metaState}
            data={dataResult}
            detail={StepsView}
            onExpandChange={handleExpandChange}
            onDataStateChange={handleDataStateChange}
            onSelectionChange={handleSelectionChange}
            onKeyDown={handleKeyDown}
            rowRender={rowRender}
            onRowClick={(event: GridRowClickEvent) => {
              if ((event.syntheticEvent as React.MouseEvent<HTMLElement>).detail === 2) {
                openJobDetails(event.dataItem);
              }
            }}
          >
            <GridColumn
              field="name"
              title="Name"
              resizable={true}
              minResizableWidth={300}
              cell={(properties: GridCustomCellProps) => {
                const metadata = getDuplicateMetadata(properties.dataItem.id);
                const summary = getDuplicateSummary(metadata);

                return (
                  <td
                    {...properties.tdProps}
                    onClick={(event) => {
                      handleCellClick(properties, event);
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <span>{properties.dataItem.name}</span>
                      {summary && (
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                          <span style={{ fontSize: "12px", color: "#666" }} title={`Source: ${summary.sourceLabel}`}>
                            Duplicated from {summary.sourceLabel}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                );
              }}
            />
            <GridColumn
              field="status"
              title={loc("status")}
              minResizableWidth={200}
              cell={(properties: GridCustomCellProps) => (
                <StatusCell
                  status={properties.dataItem.status}
                  isPauseRequested={Boolean(properties.dataItem.pendingResumeFromStepId)}
                  progress={properties.dataItem.progress}
                  isbatch={false}
                  currentStep={properties.dataItem.currentStep}
                  failedBatchesCount={properties.dataItem.failedBatchesCount}
                  showFailureWarning={true}
                  onFailureClick={() => void openFailedBatchesDialog(properties.dataItem.id, properties.dataItem.software)}
                  tdProps={properties.tdProps ?? undefined}
                  selectionChange={properties.selectionChange}
                />
              )}
              filterCell={(properties) => <DropdownFilterCell {...properties} data={Object.values(StatusEnumSchema) as string[]} defaultItem={"Select status"} onToggle={handleDropdownToggle} />}
            />
            <GridColumn
              title={loc("logs")}
              width="120px"
              minResizableWidth={110}
              filterable={false}
              cell={(properties: GridCustomCellProps) => (
                <td {...properties.tdProps} style={{ ...properties.tdProps?.style, textAlign: "left" }}>
                  <JobStatusLogsCell job={properties.dataItem} listJobsService={listJobsService} openLogs={batchArtifactsContextValue.openLogs} />
                </td>
              )}
            />

            <GridColumn
              field="pipelineType"
              title="Pipeline"
              resizable={true}
              minResizableWidth={130}
              cell={(properties: GridCustomCellProps) => (
                <td
                  {...properties.tdProps}
                  onClick={(event) => {
                    handleCellClick(properties, event);
                  }}
                >
                  {getPipelineLabel(properties.dataItem.software, properties.dataItem.pipelineType) ?? formatStepName(properties.dataItem.pipelineType ?? "")}
                </td>
              )}
              filterCell={(properties) => (
                <DropdownFilterCell
                  {...properties}
                  data={Object.values(JobPipelineTypeSchema).map((v) => formatStepName(v as string))}
                  defaultItem={"Select Pipeline"}
                  onToggle={handleDropdownToggle}
                />
              )}
            />
            <GridColumn
              field="software"
              title={loc("software")}
              resizable={true}
              minResizableWidth={130}
              filter="text"
              cell={(properties: GridCustomCellProps) => (
                <td
                  {...properties.tdProps}
                  onClick={(event) => {
                    handleCellClick(properties, event);
                  }}
                >
                  {properties.dataItem.software ?? ""}
                </td>
              )}
            />
            <GridColumn
              field="softwareVersion"
              title={loc("softwareVersion")}
              resizable={true}
              minResizableWidth={150}
              filter="text"
              cell={(properties: GridCustomCellProps) => (
                <td
                  {...properties.tdProps}
                  onClick={(event) => {
                    handleCellClick(properties, event);
                  }}
                >
                  {properties.dataItem.softwareVersion ?? ""}
                </td>
              )}
            />
            <GridColumn
              field="createdBy"
              title={loc("createdBy")}
              minResizableWidth={100}
              width={200}
              cell={(properties: GridCustomCellProps) => (
                <td
                  {...properties.tdProps}
                  onClick={(event) => {
                    handleCellClick(properties, event);
                  }}
                >
                  {properties.dataItem.createdBy ?? ""}
                </td>
              )}
            />
            <GridColumn
              field="virtualStartedAt"
              title={loc("startedAt")}
              resizable={true}
              minResizableWidth={130}
              width={200}
              filter="date"
              format="{0:dd/MM/yyyy h:mm a}"
              cell={(properties: GridCustomCellProps) => (
                <td
                  {...properties.tdProps}
                  onClick={(event) => {
                    handleCellClick(properties, event);
                  }}
                >
                  {properties.dataItem.virtualStartedAt ? formatGridDateTime(properties.dataItem.virtualStartedAt) : ""}
                </td>
              )}
            />
            <GridColumn
              title="Avg. Protein Groups"
              resizable={true}
              minResizableWidth={80}
              width={150}
              cell={(properties: GridCustomCellProps) => {
                const description = properties.dataItem.description || "";
                const proteinGroupsMatch = description.match(/Avg Profiles - Protein Groups\s*[\s\S]*?(\d{1,3}(?:,\d{3})*|\d+)/i);
                const proteinGroupsCount = proteinGroupsMatch ? proteinGroupsMatch[1] : "N/A";

                return (
                  <td
                    {...properties.tdProps}
                    title={description}
                    style={{ ...properties.tdProps?.style, cursor: description ? "help" : "default" }}
                    onClick={(event) => {
                      handleCellClick(properties, event);
                    }}
                  >
                    {proteinGroupsCount}
                  </td>
                );
              }}
            />
            <GridColumn
              title={"Results"}
              width="200%"
              minResizableWidth={220}
              cell={(properties: GridCustomCellProps) => (
                <td
                  {...properties.tdProps}
                  style={{ ...properties.tdProps?.style, textAlign: "left" }}
                  onClick={(event) => {
                    handleCellClick(properties, event);
                  }}
                >
                  {properties.dataItem.status === StatusEnumSchema.Completed && (
                    <a
                      href="#"
                      {...{ [GRID_PREVENT_SELECTION_ELEMENT]: true }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDownload(properties.dataItem);
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <Button themeColor="secondary" fillMode="link" style={{ padding: 0 }} svgIcon={downloadIcon}>
                        {properties.dataItem.precursorsCount ?? "Download"}
                      </Button>
                    </a>
                  )}
                </td>
              )}
            />
            <GridColumn
              title={"Samples"}
              width="150px"
              minResizableWidth={130}
              cell={(properties: GridCustomCellProps) => (
                <td
                  {...properties.tdProps}
                  style={{ ...properties.tdProps?.style, textAlign: "left" }}
                  onClick={(event) => {
                    handleCellClick(properties, event);
                  }}
                >
                  <Button
                    {...{ [GRID_PREVENT_SELECTION_ELEMENT]: true }}
                    themeColor="secondary"
                    fillMode="link"
                    style={{ padding: 0 }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSamplesClick(properties.dataItem);
                    }}
                    onDoubleClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    View Samples
                  </Button>
                </td>
              )}
            />
            {/* <GridColumn field="id" title="ID" minResizableWidth={150} /> */}
          </Grid>
          {/* expand advanced job details on double click  */}
          {isDoubleClickDialogVisible && selectedJob && (
            <Dialog title={`${selectedJob.name} - Job Details`} onClose={closeDialog} width={800}>
              <div style={{ padding: "10px", maxHeight: "80vh", overflowY: "auto" }}>
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "20px" }}>
                    <div>
                      <strong>Job ID:</strong> {selectedJob.id}
                    </div>
                    {selectedJob.createdAt && (
                      <div>
                        <strong>Created At:</strong> {new Date(selectedJob.createdAt).toLocaleString()}
                      </div>
                    )}
                    {selectedJob.finishedAt && (
                      <div>
                        <strong>Finished At:</strong> {new Date(selectedJob.finishedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>

                {getDuplicateMetadata(selectedJob.id) &&
                  (() => {
                    const metadata = getDuplicateMetadata(selectedJob.id)!;

                    return (
                      <>
                        <h3>Duplicated From</h3>
                        <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                          <div>
                            <strong>Job:</strong> {metadata.sourceJobName}
                          </div>
                          {metadata.sourceSoftwareVersion && (
                            <div>
                              <strong>Spectronaut Version:</strong> {metadata.sourceSoftwareVersion}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}

                {selectedJob.softwareSettingsFile && (
                  <>
                    <h3>Software Settings File</h3>
                    <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                      <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>
                        {typeof selectedJob.softwareSettingsFile === "string" ? selectedJob.softwareSettingsFile.replace(/^fasta\//, "") : JSON.stringify(selectedJob.softwareSettingsFile, null, 2)}
                      </pre>
                    </div>
                  </>
                )}

                {selectedJob.conditionSetupFile && (
                  <>
                    <h3>Condition Setup</h3>
                    <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                      <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>{selectedJob.conditionSetupFile.replace(/^fasta\//, "")}</pre>
                    </div>
                  </>
                )}

                {selectedJob.reportSettingsFile &&
                  ((typeof selectedJob.reportSettingsFile === "string" && selectedJob.reportSettingsFile !== "") ||
                    (Array.isArray(selectedJob.reportSettingsFile) && selectedJob.reportSettingsFile.length > 0)) && (
                    <>
                      <h3>Report Settings</h3>
                      <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                        <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>{formatFileListDisplay(selectedJob.reportSettingsFile, /^fasta\//)}</pre>
                      </div>
                    </>
                  )}

                {selectedJob.fastaFiles && selectedJob.fastaFiles.length > 0 && (
                  <>
                    <h3>Fasta Files</h3>
                    <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                      <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>{formatFileListDisplay(selectedJob.fastaFiles, /^fasta\//)}</pre>
                    </div>
                  </>
                )}
                {selectedJob.libraryFiles && selectedJob.libraryFiles.length > 0 && (
                  <>
                    <h3>Library Files</h3>
                    <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                      <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>{formatFileListDisplay(selectedJob.libraryFiles, /^library\//)}</pre>
                    </div>
                  </>
                )}

                {selectedJob.archiveFiles && selectedJob.archiveFiles.length > 0 && (
                  <>
                    <h3>Archive Files</h3>
                    <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                      <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>{formatFileListDisplay(selectedJob.archiveFiles)}</pre>
                    </div>
                  </>
                )}

                {selectedJob.goFile && selectedJob.goFile.length > 0 && (
                  <>
                    <h3>Gene Annotation Files</h3>
                    <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                      <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>{formatFileListDisplay(selectedJob.goFile)}</pre>
                    </div>
                  </>
                )}

                {selectedJob.description && (
                  <>
                    <h3>Results overview</h3>
                    <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                      <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>{selectedJob.description}</pre>
                    </div>
                  </>
                )}

                {selectedJob && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                    <Button
                      themeColor="primary"
                      onClick={() => {
                        closeDialog();
                        openDuplicateJobSetup(selectedJob.id);
                      }}
                    >
                      Duplicate Job And Run
                    </Button>
                  </div>
                )}
              </div>
            </Dialog>
          )}
          {selectedSampleBatchId && (
            <Dialog title={`${selectedSampleJobName ?? "Job"} samples`} onClose={closeSamplesDialog} width={"min(1200px, calc(100vw - 32px))"} height={"min(85vh, 900px)"}>
              <SamplesView dataItem={{ id: selectedSampleBatchId }} dataIndex={0} />
            </Dialog>
          )}
        </DropdownFilterFocusContext.Provider>
      </BatchArtifactsViewerContext.Provider>
    </div>
  );
};

export default JobList;

export function formatStepName(stepName: string | null | undefined, software?: SoftwareEnumSchema | null): string {
  if (!stepName) return "";

  // Engines that name a shared step differently win over the generic formatting.
  const engineLabel = getStepLabel(software, stepName);
  if (engineLabel) return engineLabel;

  // Keep "directDIA" as-is
  if (stepName === "DirectDIA") return "directDIA";

  return stepName
    .replaceAll(/([a-z])([A-Z])/g, "$1 $2") // Add space between camelCase/PascalCase
    .replaceAll(/([a-z])(\d)/g, "$1 $2") // Add space between letters and numbers
    .replaceAll(/(\d)([a-z])/g, "$1 $2") // Add space between numbers and letters
    .replaceAll(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
    .replaceAll(/\b(Dia|Sne|Psar)\b/g, (acronym) => acronym.toUpperCase()); // Ensure "DIA", "SNE" and "PSAR" remain fully capitalized
}
